import {useProductSearchStore} from "./store";
import {searchApi} from "../../../shared/api/instances/searchApi";
import {ServerSearchResponseType} from "../../../shared/api/types";
import {mapServerSearchResult} from "../libs/mapServerSearchResult";
import {useRef} from "react";
import {FilterStateType} from "./types";
import axios from "axios";

import {excludeFieldsFromResponse} from "../libs/filterResponseAdapder";
import {useWidgetSettings} from "../../../shared/context/WidgetSettingsContext";

/**
 * Параметры для запроса поиска на сервер
 */
type FetchingSearchParamsType = {
    /**
     * Строка поиска или значение подсказки
     * @type {String}
     */
    searchTerm: string;
    /**
     * Пагинация — индекс, с которого начинать загрузку
     *  @type {number}
     */
    startIndex: number;
    /**
     * Подгрузка товаров. По умолчанию false
     *  - true догрузить
     *  - false перезаписать
     *  @type {boolean}
     */
    isLoadMore?: boolean;

    /**
     * Максимальное количество элементов, которые нужно получить за один запрос.
     * По умолчанию 20
     *  @type {number}
     *
     *
     */
    limit?: number;

    /**
     * Состояние динамического Фильтра
     * Нужно сбрасивать состояние когда поиск идет по  searchTerm
     *  @type {FilterStateType}
     */
    filterState?: FilterStateType

}

export const useSearch = () => {
    const setLoading = useProductSearchStore(state => state.setLoading);
    const setSearchValue = useProductSearchStore(state => state.setSearchValue);
    const setFilterState = useProductSearchStore(state => state.setFilterState);
    const setResultSearch = useProductSearchStore(state => state.setResultSearch)
    // 🔁 текущий AbortController
    const abortControllerRef = useRef<AbortController | null>(null);
    const globalWidgetSettings = useWidgetSettings()
    const fetchingSearch = async (
        {
            searchTerm = "",
            startIndex = 0,
            isLoadMore = false,
            filterState = {},
            limit = 20
        }
        : FetchingSearchParamsType) => {
        /**
         *
         * Активируем загрузку
         */
        setLoading(true);
        // 🔴 Отменить предыдущий запрос
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        // 🔄 Новый контроллер
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            /**
             *
             * Обновляем состояние поля поиска
             */
            setSearchValue(searchTerm);
            setFilterState(filterState);
            // Получаем глобальные настройки, если они есть
            const globalFilterParams = globalWidgetSettings?.filterParams ?? {};
            const globalExcludeFieldFilter = globalWidgetSettings?.excludeFilterFiled ?? {};
            const apiChanel = globalWidgetSettings?.apiChannel ?? "dev_ifp_en";
            const response = await searchApi.post<ServerSearchResponseType>('/search', {
                    "key": "b'Ae%}9505t9v;<S2oCAXz`:59o*pQEI",
                    "channel": apiChanel,
                    "autocomplete": {},
                    "selectors": 1,
                    "suggest": 10,
                    "filters": {
                        "multi_search": {
                            "weights": {
                                "name": 4,
                                "description": 4,
                                // "type_material_name": 2,
                                "code_firms": 1,
                                "tags": 1
                            },
                            "fields": [
                                "name",
                                "description",
                                // "type_material_name",
                                "code_firms",
                                "tags"
                            ],
                            "tokens": [searchTerm]
                        },
                        ...filterState,
                        ...globalFilterParams,
                    },
                    "start_index": startIndex,
                    limit: limit,

                },
                {
                    signal: controller.signal, // ⬅️ передаём сигнал
                }
            );

            if (response.data.status === "error" || response.status !== 200) {
                setLoading(false);
                return
            }

            const {filterSelectors, ...result} = mapServerSearchResult(response.data);
            const cleanFilterSelectors = excludeFieldsFromResponse(filterSelectors, globalExcludeFieldFilter);

            setResultSearch({...result, filterSelectors: cleanFilterSelectors}, isLoadMore)
            setLoading(false);

        } catch (err) {
            if (!axios.isCancel(err)) {
                // только если ошибка НЕ Abort (🔴 Отмениненный запрос)
                setLoading(false);
            }
            //::TODO заглушка когда сервер не работает
            //    const result = mapServerSearchResult({
            //        "selectors": {
            //            "ws_available_for_sale_by_firm": {
            //                "data": {
            //                    "1": 16,
            //                    "3": 15,
            //                    "4": 15,
            //                    "12": 15,
            //                    "28": 1,
            //                    "31": 16
            //                },
            //                "type": "checkbox_list",
            //                "title": "ws_available_for_sale_by_firm",
            //                "count": 6
            //            },
            //            "band": {
            //                "type": "checkbox",
            //                "title": "band",
            //                "count": 0
            //            },
            //            "part": {
            //                "type": "checkbox",
            //                "title": "part",
            //                "count": 1
            //            },
            //            "tovar": {
            //                "type": "checkbox",
            //                "title": "tovar",
            //                "count": 0
            //            },
            //            "producer_name": {
            //                "data": {
            //                    "hafele": 15,
            //                    "westag": 1
            //                },
            //                "type": "checkbox_list",
            //                "title": "producer_name",
            //                "count": 2
            //            },
            //            "type_material_name": {
            //                "data": {
            //                    "furniture handles and hooks": 15,
            //                    "worktop 38-39 mm": 1
            //                },
            //                "type": "checkbox_list",
            //                "title": "type_material_name",
            //                "count": 2
            //            },
            //            "test_price": {
            //                "data": {
            //                    "min": "57.03",
            //                    "max": "2133.76"
            //                },
            //                "type": "range",
            //                "title": "test_price",
            //                "count": 2
            //            }
            //        },
            //        "count": 16,
            //        "start_from": 0,
            //        "channel": {
            //            "link": "https://www.ifurn.pro.en",
            //            "id": "dev_ifp_en"
            //        },
            //        "select_range": {},
            //        "from_other_channels": {},
            //        "statistics": {},
            //        "autocomplete": [],
            //        "suggest": "",
            //        "items": [
            //            {
            //                "code_firms": "28:4553 , 1:4553 , 31:4553",
            //                "description": "Tabletop SL 239 salo beige Westag 4100 600 39. Color: yellow. Texture: matte with a slight texture reminiscent of an orange peel. Dimensions: 4100x600x39 mm. Ideal for kitchens and bathrooms, resistant to moisture and wear.",
            //                "name": "SL 239 salome beige Westag 4100 600 39 Worktops",
            //                "tags": "sl, 239, salome, beige",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    28,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "503.22",
            //                "band": false,
            //                "part": true,
            //                "tovar": false,
            //                "producer_name": [
            //                    "westag"
            //                ],
            //                "type_material_name": [
            //                    "worktop 38-39 mm"
            //                ],
            //                "hash_md5": "d12e7cc780de3d0536473769958c183c",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 57571
            //            },
            //            {
            //                "code_firms": "1:24476 , 3:24476 , 12:24476 , 4:36805 , 31:24476",
            //                "description": "Mortise handle SLV 256x80, Hafele",
            //                "name": "Mortise handle SLV 256x80, Hafele",
            //                "tags": "aluminum, silver, mortise",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "233.85",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "cbbaeeb1ea3597278be7fe9f2aa84e92",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46101
            //            },
            //            {
            //                "code_firms": "1:24596 , 3:24596 , 12:24596 , 4:36819 , 31:24596",
            //                "description": "Profile handle SLV 2500 mm, Hafele",
            //                "name": "Profile handle SLV 2500 mm, Hafele",
            //                "tags": "silver, profile",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "103.64",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "6139f28357bbf42635bf7ca3979f6601",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46115
            //            },
            //            {
            //                "code_firms": "1:24588 , 3:24588 , 12:24588 , 4:36820 , 31:24588",
            //                "description": "Profile handle SLV 295 mm, Hafele",
            //                "name": "Profile handle SLV 295 mm, Hafele",
            //                "tags": "silver, profile",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "437.35",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "7feb30f98cf612bad604c6cc57ea2d15",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46116
            //            },
            //            {
            //                "code_firms": "1:24589 , 3:24589 , 12:24589 , 4:36821 , 31:24589",
            //                "description": "Profile handle SLV 445 mm, Hafele",
            //                "name": "Profile handle SLV 445 mm, Hafele",
            //                "tags": "silver, profile",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "1396.71",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "896c47fd85ac285416a064beb0233ddc",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46117
            //            },
            //            {
            //                "code_firms": "1:24590 , 3:24590 , 12:24590 , 4:36822 , 31:24590",
            //                "description": "Profile handle SLV 595 mm, Hafele",
            //                "name": "Profile handle SLV 595 mm, Hafele",
            //                "tags": "silver, profile",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "384.54",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "d09838450c9b8d708078db667a56e98c",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46118
            //            },
            //            {
            //                "code_firms": "1:24591 , 3:24591 , 12:24591 , 4:36823 , 31:24591",
            //                "description": "Profile handle SLV 795 mm, Hafele",
            //                "name": "Profile handle SLV 795 mm, Hafele",
            //                "tags": "silver, profile",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "753.61",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "b4916d80c09775cbba617fe1df93b4d6",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46119
            //            },
            //            {
            //                "code_firms": "1:24592 , 3:24592 , 12:24592 , 4:36824 , 31:24592",
            //                "description": "Profile handle SLV 895 mm, Hafele",
            //                "name": "Profile handle SLV 895 mm, Hafele",
            //                "tags": "silver, profile",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "744.09",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "3ea0669855cd9786d0a435eb7a849731",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46120
            //            },
            //            {
            //                "code_firms": "1:24468 , 3:24468 , 12:24468 , 4:36804 , 31:24468",
            //                "description": "Mortise handle SLS stainless steel, 152x87, Hafele",
            //                "name": "Mortise handle SLS stainless steel, 152x87, Hafele",
            //                "tags": "zamak, steel, mortise, 27, stainless",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "495.05",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "750f386fc13ebc7f7af6c6194e512174",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46100
            //            },
            //            {
            //                "code_firms": "1:24477 , 3:24477 , 12:24477 , 4:36814 , 31:24477",
            //                "description": "Folding handle SLS 160x25, Hafele",
            //                "name": "Folding handle SLS 160x25, Hafele",
            //                "tags": "aluminum, silver, mortise",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "283.83",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "ef3116c0a9edfb5521201c73fe2b19a6",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46110
            //            },
            //            {
            //                "code_firms": "1:23959 , 3:23959 , 12:23959 , 4:36825 , 31:23959",
            //                "description": "Profile handle, stainless steel steel SLS 445 mm, Hafele",
            //                "name": "Profile handle, stainless steel steel SLS 445 mm, Hafele",
            //                "tags": "steel, profile",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "103.54",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "794ab7e0bb98e02e9d9cd68a823fda65",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46121
            //            },
            //            {
            //                "code_firms": "1:24055 , 3:24055 , 12:24055 , 4:36826 , 31:24055",
            //                "description": "Profile handle, stainless steel steel SLS 495 mm, Hafele",
            //                "name": "Profile handle, stainless steel steel SLS 495 mm, Hafele",
            //                "tags": "steel, profile, stainless",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "1130.58",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "06c0793c091e96f51b0b8130e92915e2",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46122
            //            },
            //            {
            //                "code_firms": "1:23960 , 3:23960 , 12:23960 , 4:36827 , 31:23960",
            //                "description": "Profile handle, stainless steel steel SLS 595 mm, Hafele",
            //                "name": "Profile handle, stainless steel steel SLS 595 mm, Hafele",
            //                "tags": "aluminum, steel, profile, stainless",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "311.95",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "e91de6b0cc7863ede6e5aff162b79956",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46123
            //            },
            //            {
            //                "code_firms": "1:23961 , 3:23961 , 12:23961 , 4:36828 , 31:23961",
            //                "description": "Profile handle, stainless steel steel SLS 795 mm, Hafele",
            //                "name": "Profile handle, stainless steel steel SLS 795 mm, Hafele",
            //                "tags": "aluminum, steel, profile, stainless",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "2068.57",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "9c9fa090abdd6cdaf4f5439727d1102b",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 46124
            //            },
            //            {
            //                "code_firms": "1:28915 , 3:28915 , 12:28915 , 4:37272 , 31:28915",
            //                "description": "Mortise handle SLS chrome, 189x24, Hafele",
            //                "name": "Mortise handle SLS chrome, 189x24, Hafele",
            //                "tags": "chromium, mortise, 96",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "1450.79",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "8bcbbdf83a6340a6d763caa67c60e484",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 48896
            //            },
            //            {
            //                "code_firms": "1:27783 , 3:27783 , 12:27783 , 4:37222 , 31:27783",
            //                "description": "Profile handle, stainless steel SLS steel 295 mm, Hafele",
            //                "name": "Profile handle, stainless steel SLS steel 295 mm, Hafele",
            //                "tags": "steel, profile, stainless",
            //                "ws_available_for_sale_by_firm": [
            //                    1,
            //                    3,
            //                    4,
            //                    12,
            //                    31
            //                ],
            //                "additional_name_by_firm": [],
            //                "test_price": "482.65",
            //                "band": false,
            //                "part": false,
            //                "tovar": false,
            //                "producer_name": [
            //                    "hafele"
            //                ],
            //                "type_material_name": [
            //                    "furniture handles and hooks"
            //                ],
            //                "hash_md5": "0ed852d2abb605d27d578d1c69261734",
            //                "created_at": 1745511053,
            //                "updated_at": 1745511053,
            //                "id": 48604
            //            }
            //        ],
            //
            //    } as unknown as ServerSearchResponseType);
            //    setResultSearch(result, isLoadMore)
        }


    }


    return {fetchingSearch}

}